import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { HardDrive, ChevronRight, RefreshCw, Download, Loader2, AlertTriangle, Filter } from 'lucide-react';

import {
    fetchITDepFinancialYears, fetchITDepreciationReport,
    selectITDepYears, selectITDepYearsLoading,
    selectITDepData, selectITDepDataLoading, selectITDepDataError,
} from '../../slices/ReportsSlice/itDepreciationReportSlice';

const inputCls = 'w-full px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent';
const td = 'px-2 py-1 text-xs text-gray-700 dark:text-gray-200 whitespace-nowrap';

const COLUMNS = [
    { key: 'TRANSACTION_id',     label: 'Transaction Id' },
    { key: 'Actual_Invoice',     label: 'Actual Invoice' },
    { key: 'ItemCode',           label: 'Item Code' },
    { key: 'Assetname',          label: 'Asset Name' },
    { key: 'Subdca_code',        label: 'Subdca Code' },
    { key: 'Asset_category',     label: 'Asset Category' },
    { key: 'Purcahse_Price',     label: 'Purchased Price', right: true },
    { key: 'FY_Opening_bal',     label: 'FY Opening Balance', right: true },
    { key: 'Current_FY_Addition',label: 'Current FY Addition', right: true },
    { key: 'Totalvalue',         label: 'Total Value', right: true },
    { key: 'Purchased_date',     label: 'Date of Purchase' },
    { key: 'Deletion_value',     label: 'Deletion Value', right: true },
    { key: 'Deletion_date',      label: 'Deletion Date' },
    { key: 'remaining_days',     label: 'Remaining Days', right: true },
    { key: 'valid_days',         label: 'Valid Days', right: true },
    { key: 'percentage',         label: 'Dep Percentage', right: true },
    { key: 'Dep_Amount',         label: 'Depreciation Value', right: true },
    { key: 'Bal_value',          label: 'Balance Value', right: true },
    { key: 'Sold_price',         label: 'Sold Price', right: true },
    { key: '__gainLoss',         label: 'Gain/Loss Value', right: true },
    { key: '__closingBal',       label: 'FY Closing Balance', right: true },
];

const ITDepreciationReport = () => {
    const dispatch = useDispatch();
    const years = useSelector(selectITDepYears);
    const yearsLoading = useSelector(selectITDepYearsLoading);
    const data = useSelector(selectITDepData);
    const dataLoading = useSelector(selectITDepDataLoading);
    const dataError = useSelector(selectITDepDataError);

    const [year, setYear] = React.useState('');
    const [ranYear, setRanYear] = React.useState('');

    useEffect(() => { dispatch(fetchITDepFinancialYears()); }, [dispatch]);
    useEffect(() => { if (dataError) toast.error(dataError); }, [dataError]);

    const runReport = () => {
        if (!year) { toast.warning('Please select a Year.'); return; }
        setRanYear(year);
        dispatch(fetchITDepreciationReport(year));
    };

    const handleRefresh = () => { if (ranYear) dispatch(fetchITDepreciationReport(ranYear)); };

    const handleExport = () => {
        if (data.length === 0) { toast.warning('No data to export'); return; }
        const rows = data.map((r) => {
            const out = {};
            COLUMNS.forEach((c) => { out[c.label] = c.key.startsWith('__') ? '' : r[c.key]; });
            return out;
        });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'IT Depreciation');
        XLSX.writeFile(wb, `IT_Depreciation_Report_${ranYear}_${new Date().toISOString().slice(0, 10)}.xlsx`);
        toast.success('Excel downloaded');
    };

    return (
        <div className="space-y-3 p-4 text-xs">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <div className="p-1.5 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-lg">
                                <HardDrive className="h-4 w-4 text-white" />
                            </div>
                            IT Depreciation Report
                        </h1>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Asset-wise IT depreciation for the selected financial year</p>
                        <nav className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-1">
                            <span>Reports</span><ChevronRight className="w-3 h-3" /><span className="text-gray-700 dark:text-gray-200">IT Depreciation Report</span>
                        </nav>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={handleRefresh} disabled={dataLoading || !ranYear} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <RefreshCw className={`h-3.5 w-3.5 ${dataLoading ? 'animate-spin' : ''}`} />Refresh
                        </button>
                        <button onClick={handleExport} disabled={data.length === 0} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <Download className="h-3.5 w-3.5" />Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-1.5 mb-2">
                    <Filter className="h-3.5 w-3.5 text-indigo-600" />
                    <h2 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Filters</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Year</label>
                        <select className={inputCls} value={year} onChange={(e) => setYear(e.target.value)} disabled={yearsLoading}>
                            <option value="">-Please Select-</option>
                            {years.map((y, i) => <option key={i} value={y.Year}>{y.YearValue}</option>)}
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button onClick={runReport} disabled={dataLoading} className="w-full px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-60">View</button>
                    </div>
                </div>
            </div>

            {/* Report */}
            {dataLoading ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-600" /><span className="text-xs text-gray-500 dark:text-gray-400">Loading report…</span>
                </div>
            ) : dataError ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10 text-red-500">
                    <AlertTriangle className="h-5 w-5" /><span className="text-xs">{dataError}</span>
                </div>
            ) : ranYear && data.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 py-10 text-center text-xs text-gray-400">No data found.</div>
            ) : data.length > 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                        <h2 className="text-xs font-bold text-gray-700 dark:text-gray-300">Asset Depreciation Detail</h2>
                    </div>
                    <div className="overflow-auto max-h-[70vh]">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gradient-to-r from-indigo-600 to-violet-600 sticky top-0 z-10">
                                <tr>
                                    {COLUMNS.map((c) => (
                                        <th key={c.key} className="px-2 py-1.5 text-left text-[10px] font-bold text-white uppercase whitespace-nowrap">{c.label}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {data.map((row, i) => (
                                    <tr key={i} className="hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors">
                                        {COLUMNS.map((c) => (
                                            <td key={c.key} className={`${td} ${c.right ? 'text-right' : ''}`}>
                                                {c.key.startsWith('__') ? '—' : (row[c.key] || '—')}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default ITDepreciationReport;
